import React, { useEffect, useState } from 'react';

import { containers } from '@ecosystem/newron-design-system';
import { makeLabel } from '../common';
import { Cell, Entity, ContextSelectorWidth, SelectorPosition } from '../types';
import { ContextEntry } from './ContextEntry';
import { ContextSelectorMenu } from './ContextSelectorMenu';
import { MultiCellSelectorContainer } from './MultiCellSelectorContainer';

import './MultiCellSelector.css';

const { Tray } = containers;

export type MultiCellSelectorProps = {
  onApply: (selected: Entity[]) => void;
  cells: Cell[];
  isLoading: boolean;
  selectedCells: Entity[];
  position?: SelectorPosition;
  showLastMonth?: boolean;
  showLastWeek?: boolean;
  primaryBtnText?: string;
  customLabel?: string;
  isDefaultOpen?: boolean;
  isFirstRender?: boolean;
  width?: ContextSelectorWidth;
};

export const MultiCellSelector = ({
  onApply,
  cells,
  isLoading,
  position,
  selectedCells,
  showLastMonth = true,
  showLastWeek = true,
  primaryBtnText,
  customLabel = 'Select...',
  isDefaultOpen,
  isFirstRender,
  width = ContextSelectorWidth.max,
}: MultiCellSelectorProps) => {
  const [expanded, setExpanded] = useState<boolean>(isDefaultOpen || false);
  const labels = selectedCells?.length ? makeLabel(selectedCells) : customLabel;
  // Define tray context. It will have value if this component is child of Tray component.
  const trayContext = Tray.useTrayContext(false);

  // If the multi cell selector is opened set the showBiggerWidthChild state to true.
  // Else set it to false.
  useEffect(() => {
    if (trayContext?.setShowBiggerWidthChild) {
      trayContext.setShowBiggerWidthChild(expanded);
    }
  }, [trayContext, expanded]);

  useEffect(() => {
    setExpanded(isDefaultOpen);
  }, [isDefaultOpen]);

  const handleClick = (value: boolean) => {
    if (!isDefaultOpen) {
      setExpanded(value);
    }
  };

  return (
    <ContextEntry
      expanded={expanded}
      onClick={handleClick}
      isLoading={isLoading}
      labels={labels}
      isFirstRender={isFirstRender}
      width={width}
      position={position}
    >
      <ContextSelectorMenu position={position}>
        <MultiCellSelectorContainer
          cells={cells}
          onClose={() => setExpanded(false)}
          onApply={onApply}
          selected={selectedCells}
          isLoading={isLoading}
          showLastMonth={showLastMonth}
          showLastWeek={showLastWeek}
          primaryBtnText={primaryBtnText}
          isCancelDisabled={isFirstRender}
        />
      </ContextSelectorMenu>
    </ContextEntry>
  );
};
